import { Injectable } from '@angular/core';
import { DatabaseService } from '../storage/database.service';
import { SmartenitPlugin } from './smartenit-plugin';
import { OnOffPlugin } from './on-off/on-off.plugin';
import { SimpleMeteringServerPlugin } from './simple-metering-server/simple-metering-server.plugin';
import { ElectricalMeasurementServerPlugin } from './electrical-measurement-server/electrical-measurement-server.plugin';
import { DoorLockPlugin } from './door-lock-server/door-lock.plugin';
import { LevelControlServerPlugin } from './level-control-server/level-control-server.plugin';
import { DiscoverPlugin } from './discover/discover.plugin';
import { BasicServerPlugin } from './basic-server/basic-server.plugin';
import { OnOffArrayServerPlugin } from './on-off-array-server/on-off-array-server.plugin';
import { ThermostatServerPlugin } from './thermostat-server/thermostat-server.plugin';
import { WaterHeaterServerPlugin } from './water-heater-server/water-heater-server.plugin';
import { OnOffClientPlugin } from './on-off-client/on-off-client.plugin';
import { IASZoneServerPlugin } from './iaszone-server/iaszone-server.plugin';
import { IASWDServerPlugin } from './iaswd-server/iaswd-server.plugin';
import { IASWDClientPlugin } from './iaswd-client/iaswd-client.plugin';
import { IASACEServerPlugin } from './iasace-server/iasace-server.plugin';
import { NotificationServerPlugin } from './notification-server/notification-server.plugin';
import { PowerConfigurationServerPlugin } from './power-configuration-server/power-configuration-server.plugin';
import { PressureMeasurementServerPlugin } from './pressure-measurement-server/pressure-measurement-server.plugin';
import { TemperatureMeasurementServerPlugin } from './temperature-measurement-server/temperature-measurement-server.plugin';
import { RelativeHumidityMeasurementServerPlugin } from './relative-humidity-measurement-server/relative-humidity-measurement-server.plugin';
import { FanControlServerPlugin } from './fan-control-server/fan-control-server.plugin';
import { EnergyManagementPlugin } from './energy-management/energy-management.plugin';
import { ColorControlServerPlugin } from './color-control-server/color-control-server.plugin';
import { TimeServerPlugin } from './time-server/time-server.plugin';
import { EVSEServerPlugin } from './evse/evse-server.plugin';
import { AnalogInputPlugin } from './analog-input/analog-input.plugin';
import { BinaryInputPlugin } from './binary-input/binary-input.plugin';
import { SystemServerPlugin } from './system-server/system-server.plugin';
import { CIDSystemServerPlugin } from './cid-system-server/cid-system-server.plugin';
import { MessagingServerPlugin } from './messaging-server/messaging-server.plugin';

import { OccupancySensingServerPlugin } from './occupancy-sensing-server/occupancy-sensing-server.plugin';
import { OTAClientPlugin } from './ota-client/ota-client.plugin';

import { DeviceModel } from '../models/device.model';
import { Model } from '../common/model';
import { SmartenitTranslationService } from '../common/translation/smartenit-translation.service';
import { TemperatureConverterService } from '../common/temperature-converter.service';
import { ActionsService } from '../resources/actions.service';
import { EffectsService } from '../resources/effects.service';
import { ConditionsService } from '../resources/conditions.service';
import { DevicesService } from '../resources/devices.service';
import { MetricsService } from '../resources/metrics.service';
import { PluginUtilsService } from './plugin-utils.service';
import { IlluminanceMeasurementServerPlugin } from './iluminance-measurement-server/iluminance-measurement-server.plugin';
import { IdentifyServerPlugin } from './identify-server/identify-server.plugin';
import { SmtElecMeasServerPlugin } from './smt-electrical-measurement-server/smt-electrical-measurement-server.plugin';
import { DRLCClientPlugin } from './drlc/drlc-plugin-client';
import { MeteringOnOffServerPlugin } from './metering-on-off-server/metering-on-off-server.plugin';
import { ActiveGatewayControlServerPlugin } from './active-gateway-control-server/active-gateway-control-server.plugin';
import { ScheduleServerPlugin } from './scheduler-server/scheduler-server.plugin';

const plugins: any = {
  ActiveGatewayControlServerPlugin,
  AnalogInputPlugin,
  BasicServerPlugin,
  BinaryInputPlugin,
  CIDSystemServerPlugin,
  ColorControlServerPlugin,
  DRLCClientPlugin,
  DiscoverPlugin,
  DoorLockServerPlugin: DoorLockPlugin,
  EVSEServerPlugin,
  ElectricalMeasurementServerPlugin,
  EnergyManagementPlugin,
  FanControlServerPlugin,
  IASACEServerPlugin,
  IASWDClientPlugin,
  IASWDServerPlugin,
  IASZoneServerPlugin,
  IdentifyServerPlugin,
  IlluminanceMeasurementServerPlugin,
  LevelControlServerPlugin,
  MessagingServerPlugin,
  MeteringOnOffServerPlugin,
  NotificationServerPlugin,
  OTAClientPlugin,
  OccupancySensingServerPlugin,
  OnOffArrayServerPlugin,
  OnOffClientPlugin,
  OnOffPlugin,
  PowerConfigurationServerPlugin,
  PressureMeasurementServerPlugin,
  RelativeHumidityMeasurementServerPlugin,
  ScheduleServerPlugin,
  SimpleMeteringServerPlugin,
  SmtElecMeasServerPlugin,
  SystemServerPlugin,
  TemperatureMeasurementServerPlugin,
  ThermostatServerPlugin,
  TimeServerPlugin,
  WaterHeaterServerPlugin
};

@Injectable()
export class PluginFactoryService {
  private _plugins: any;

  constructor(
    public dbService: DatabaseService,
    public actionsService: ActionsService,
    public effectsService: EffectsService,
    public conditionsService: ConditionsService,
    public pluginUtilsService: PluginUtilsService,
    public metricsService: MetricsService,
    public translationService: SmartenitTranslationService,
    public temperatureConverterService: TemperatureConverterService
  ) {
    this._plugins = {};
  }

  private static getPluginKey(
    deviceId: string,
    componentId: string,
    processorName: string
  ): string {
    return [deviceId, 'comp', componentId, 'proc', processorName].join('_');
  }

  public createPlugin(
    pluginName: string,
    componentId: string,
    processorName: string,
    resource: Model,
    devicesService: DevicesService
  ): SmartenitPlugin | null {
    const key = PluginFactoryService.getPluginKey(
      resource._id,
      componentId,
      processorName
    );

    if (this._plugins.hasOwnProperty(key)) {
      return this._plugins[key];
    } else {
      if (plugins.hasOwnProperty(pluginName + 'Plugin')) {
        const pluginClass = plugins[pluginName + 'Plugin'];

        const newPlugin: SmartenitPlugin = new pluginClass(
          pluginName,
          componentId,
          processorName,
          resource,
          this.dbService,
          devicesService,
          this.actionsService,
          this.conditionsService,
          this.effectsService,
          this.pluginUtilsService,
          this.metricsService,
          this.translationService,
          this.temperatureConverterService
        );

        this._plugins[key] = newPlugin;
        return newPlugin;
      }
    }

    return null;
  }

  public removePluginsForDevice(deviceId: string) {
    this._plugins = Object.keys(this._plugins)
      .filter((key) => !key.startsWith(deviceId))
      .reduce((obj: any, key: any) => {
        obj[key] = this._plugins[key];
        return obj;
      }, {});
  }

  public clearPlugins() {
    this._plugins = {};
  }
}
